import { useTranslation } from 'next-i18next';
import type { IconNames } from '@dx-ui/osc-icon';
import { Icon } from '@dx-ui/osc-icon';
import { Heading, Surface, Link, Text } from '@dx-ui/global-ui-prism';
import { env } from '@dx-ui/framework-env';
import { PhoneNumberLink, PhoneNumberSection } from '..';
import { useRouter } from 'next/router';

export type Contact = {
  numbers: { label: string; phone: string }[];
  phoneText: string;
  title: string;
};

export type ContactInfoOverrides = Contact & {
  /** Optional additional content rendered below Contact section */
  footer?: React.ReactNode;
};

export type ContactInfo = {
  honors: Contact;
  brand: Contact;
  luxury: Contact;
};

export type LinkLabels = {
  home: string;
  user: string;
  search: string;
};

export type ErrorPageProps = {
  /** statusCode of error */
  statusCode?: number;
  /** Should contact block be wrapped with Call Me */
  shouldContactBeWrappedWithCallMe?: boolean;
  /** Override the default contact section displayed on the error page */
  contactInfoOverrides?: ContactInfoOverrides;
};

const OHW_BASE_URL = env('OHW_BASE_URL', 'https://www.hilton.com/');
const OHW_HOST = new URL(OHW_BASE_URL).host;

/** Technical error page, Not Found error page and Unknown error page that could be used as a Fallback UI. */
export const ErrorPage = ({
  shouldContactBeWrappedWithCallMe = false,
  statusCode,
  contactInfoOverrides,
}: ErrorPageProps) => {
  const { t, ready } = useTranslation('osc-error-page');
  const { locale } = useRouter();

  const NAVIGATION_URLS: Record<keyof LinkLabels, string> = {
    search: `https://${OHW_HOST}/${locale}/search/find-hotels/`,
    user: `https://${OHW_HOST}/${locale}/hilton-honors/guest/my-account/`,
    home: `https://${OHW_HOST}/${locale}/`,
  };

  const helpfulLinks = Object.keys(NAVIGATION_URLS).map((key) => ({
    key,
    label: t(`helpfulLinkLabels.${key as keyof typeof NAVIGATION_URLS}`),
    url: NAVIGATION_URLS[key as keyof typeof NAVIGATION_URLS],
    icon: key as (typeof IconNames)[number],
  }));

  const { honors, brand } = t('contactInfo', { returnObjects: true }) as ContactInfo;

  const error = statusCode === 404 ? '404' : statusCode === 500 ? 'technical' : 'unknown';

  return ready ? (
    <Surface type="base">
      <div className="h-screen">
        <div className="container flex flex-col gap-4 py-4 sm:items-center sm:justify-center sm:gap-8 sm:py-10">
          <Heading className="sm:text-center" level={1} size="3xl">
            {t(`${error}.heading`)}
          </Heading>
          {error !== 'unknown' && t(`${error}.subheading`) ? (
            <Text data-testid="pageSubTitle">{t(`${error}.subheading`).toUpperCase()}</Text>
          ) : null}
          <Heading className="sm:text-center" size="md">
            {t(`${error}.description`)}
          </Heading>

          <Heading className="font-bold" size="lg">
            {t(`${error}.action`)}
          </Heading>

          {/* Helpful navigation links */}
          <nav>
            <ul className="flex flex-col flex-wrap items-start sm:flex-row sm:px-8 md:px-12 lg:px-16">
              {Object.values(helpfulLinks).map((link) => (
                <li key={link.label} className="flex justify-center px-1 sm:flex-1">
                  <Link
                    url={link.url}
                    className="flex flex-nowrap items-center justify-center whitespace-nowrap py-2 "
                  >
                    <span className="me-2 block" aria-hidden>
                      <Icon name={link.icon} className="w-5" />
                    </span>
                    {link.label}
                  </Link>
                </li>
              ))}
            </ul>
          </nav>
          {/* Contact block with optional call-me wrapper */}
          <div
            className="w-full"
            {...(shouldContactBeWrappedWithCallMe && { id: 'callme-wrapper' })}
          >
            <div className="relative grid grid-cols-1 gap-2 sm:grid-cols-2 sm:items-start sm:justify-items-center">
              {contactInfoOverrides ? (
                <PhoneNumberSection title={contactInfoOverrides.title}>
                  {contactInfoOverrides.numbers.map((number) => (
                    <PhoneNumberLink
                      key={number.label}
                      hint={number.label}
                      url={`tel:${number.phone}`}
                      label={number.phone}
                    />
                  ))}
                  {contactInfoOverrides.footer ? (
                    <div className="mt-2 sm:text-center">{contactInfoOverrides.footer}</div>
                  ) : null}
                </PhoneNumberSection>
              ) : (
                <PhoneNumberSection title={t('contactInfo.brand.title')}>
                  {brand.numbers.map((number) => (
                    <PhoneNumberLink
                      key={number.label}
                      hint={number.label}
                      url={`tel:${number.phone}`}
                      label={number.phone}
                    />
                  ))}
                </PhoneNumberSection>
              )}
              <div className="absolute inset-y-0 left-1/2 hidden border border-surface sm:block" />
              <PhoneNumberSection title={t('contactInfo.honors.title')}>
                {honors.numbers.map((number) => (
                  <PhoneNumberLink
                    key={number.label}
                    hint={number.label}
                    url={`tel:${number.phone}`}
                    label={number.phone}
                  />
                ))}
              </PhoneNumberSection>
            </div>
          </div>
          <div className="absolute bottom-16 right-0" id="chatnow-wrapper" />
        </div>
      </div>
    </Surface>
  ) : null;
};

export default ErrorPage;
